import React, { useState, useEffect } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import "./EducationPurpose.css";
import { API_BASE_URL } from "../config";

// MUI DatePicker imports
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import TextField from "@mui/material/TextField";

const EducationPurpose = () => {
  const [form, setForm] = useState({
    visit_type: "",
    role: "",
    name: "",
    email: "",
    register_number: "",
    phone: "",
    department: "",
    year: "",
    college: "",
    purpose: "",
    designation: "",
    employeeId: "",
    preferred_location: "",
    visit_date: null,
    training_duration: "",
  });

  const [errors, setErrors] = useState({});
  const [excelFile, setExcelFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [ivBanner, setIvBanner] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/ivform/banner`)
      .then((res) => res.json())
      .then((data) => {
        const baseURL = API_BASE_URL.replace("/api", "");
        setIvBanner(`${baseURL}${data.image}`);
      })
      .catch(() => console.log("IV banner load failed"));
  }, []);

  // -----------------------------------------
  // Excel Upload
  // -----------------------------------------
  const handleExcelUpload = (e) => {
    const file = e.target.files[0];
    setExcelFile(file);

    const reader = new FileReader();
    reader.readAsArrayBuffer(file);

    reader.onload = (event) => {
      const data = new Uint8Array(event.target.result);
      const workbook = XLSX.read(data, { type: "array" });

      const sheetName = workbook.SheetNames[0];
      const sheetData = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName]);

      console.log("Excel Data:", sheetData);
    };
  };

  // -----------------------------------------
  // Handle Input Change
  // -----------------------------------------
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // -----------------------------------------
  // Validation
  // -----------------------------------------
  const validateForm = () => {
    let newErrors = {};
    const f = (v) => (v ? v.trim() : "");

    if (!form.visit_type) newErrors.visit_type = "Select visit type";
    if (!form.role) newErrors.role = "Select your role";

    if (!f(form.name)) newErrors.name = "Name required";

    if (!f(form.email)) newErrors.email = "Email required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      newErrors.email = "Enter valid email";

    if (!f(form.phone)) newErrors.phone = "Phone number required";
    else if (!/^\d{10}$/.test(form.phone))
      newErrors.phone = "Phone must be 10 digits";

    // STUDENT validation
    if (form.role === "student") {
      if (!f(form.register_number))
        newErrors.register_number = "Register Number required";

      if (!f(form.department)) newErrors.department = "Department required";
      if (!f(form.year)) newErrors.year = "Year required";
      if (!f(form.college)) newErrors.college = "College required";
    }

    // STAFF validation
    if (form.role === "staff") {
      if (!f(form.designation)) newErrors.designation = "Designation required";
      if (!f(form.employeeId)) newErrors.employeeId = "Employee ID required";
      if (!f(form.department)) newErrors.department = "Department required";
      if (!f(form.college)) newErrors.college = "College required";
    }

    // Purpose
    if (!f(form.purpose)) newErrors.purpose = "Purpose required";

    // Preferred Location
    if (!f(form.preferred_location))
      newErrors.preferred_location = "Preferred Location required";

    // Industrial Visit → Date validation
    if (form.visit_type === "industrial_visit") {
      if (!form.visit_date) {
        newErrors.visit_date = "Select a valid Friday or Saturday";
      } else {
        // Additional validation: Check if the selected date is Friday or Saturday
        const selectedDay = new Date(form.visit_date).getDay();
        if (selectedDay !== 5 && selectedDay !== 6) {
          newErrors.visit_date = "Only Friday or Saturday dates are allowed";
        }
      }
    }

    // Inplant Training duration validation
    if (form.visit_type === "inplant_training") {
      if (!form.training_duration)
        newErrors.training_duration = "Select training duration";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // -----------------------------------------
  // Submit Form
  // -----------------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    if (submitting) return;

    setSubmitting(true);

    // Remove irrelevant data
    if (form.visit_type === "industrial_visit") delete form.training_duration;
    if (form.visit_type === "inplant_training") form.visit_date = null;

    const finalData = new FormData();
    finalData.append("formData", JSON.stringify(form));
    if (excelFile) finalData.append("excelFile", excelFile);

    try {
      await axios.post(`${API_BASE_URL}/api/ivform/createForm`, finalData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      alert("✅ Submitted Successfully!");

      setForm({
        visit_type: "",
        role: "",
        name: "",
        email: "",
        register_number: "",
        phone: "",
        department: "",
        year: "",
        college: "",
        purpose: "",
        designation: "",
        employeeId: "",
        preferred_location: "",
        visit_date: null,
        training_duration: "",
      });

      setExcelFile(null);
      setErrors({});
    } catch (error) {
      console.error("Submission Error:", error);
      alert("❌ Error submitting form");
    } finally {
      setSubmitting(false);
    }
  };

  // -----------------------------------------
  // UI
  // -----------------------------------------
  return (
    <div className="education-container">
      <div className="education-image">
        <img
          src={ivBanner || `${API_BASE_URL}/uploads/iv_image.jpg`}
          alt="IV Banner"
        />
      </div>

      <div className="education-form">
        <h2>
          {form.visit_type === "industrial_visit"
            ? "Industrial Visit Form"
            : form.visit_type === "inplant_training"
            ? "Inplant Training Form"
            : "Industrial Visit & Inplant Training Form"}
        </h2>

        <form onSubmit={handleSubmit}>
          {/* Visit type */}
          <select
            name="visit_type"
            value={form.visit_type}
            onChange={handleChange}
          >
            <option value="">Select Visit Type</option>
            <option value="industrial_visit">Industrial Visit</option>
            <option value="inplant_training">Inplant Training</option>
          </select>
          {errors.visit_type && <p className="error">{errors.visit_type}</p>}

          {/* Role */}
          <select
            name="role"
            value={form.role}
            onChange={handleChange}
            disabled={!form.visit_type}
          >
            <option value="">Select Role</option>
            <option value="student">Student</option>
            <option value="staff">Staff</option>
          </select>
          {errors.role && <p className="error">{errors.role}</p>}

          {/* Name */}
          <input
            type="text"
            name="name"
            placeholder="Full Name"
            value={form.name}
            onChange={handleChange}
          />
          {errors.name && <p className="error">{errors.name}</p>}

          {/* Email */}
          <input
            type="email"
            name="email"
            placeholder="Email"
            value={form.email}
            onChange={handleChange}
          />
          {errors.email && <p className="error">{errors.email}</p>}

          {/* Phone */}
          <input
            type="text"
            name="phone"
            placeholder="Phone Number"
            value={form.phone}
            onChange={handleChange}
          />
          {errors.phone && <p className="error">{errors.phone}</p>}

          {/* Department */}
          <input
            type="text"
            name="department"
            placeholder="Department"
            value={form.department}
            onChange={handleChange}
          />
          {errors.department && <p className="error">{errors.department}</p>}

          {/* Student fields */}
          {form.role === "student" && (
            <>
              <input
                type="text"
                name="year"
                placeholder="Year (1-4)"
                value={form.year}
                onChange={handleChange}
              />
              {errors.year && <p className="error">{errors.year}</p>}

              <input
                type="text"
                name="register_number"
                placeholder="Register Number"
                value={form.register_number}
                onChange={handleChange}
              />
              {errors.register_number && (
                <p className="error">{errors.register_number}</p>
              )}

              <input
                type="text"
                name="college"
                placeholder="College Name"
                value={form.college}
                onChange={handleChange}
              />
              {errors.college && <p className="error">{errors.college}</p>}
            </>
          )}

          {/* Staff fields */}
          {form.role === "staff" && (
            <>
              <input
                type="text"
                name="designation"
                placeholder="Designation"
                value={form.designation}
                onChange={handleChange}
              />
              {errors.designation && (
                <p className="error">{errors.designation}</p>
              )}

              <input
                type="text"
                name="employeeId"
                placeholder="Employee ID"
                value={form.employeeId}
                onChange={handleChange}
              />
              {errors.employeeId && (
                <p className="error">{errors.employeeId}</p>
              )}

              <input
                type="text"
                name="college"
                placeholder="College Name"
                value={form.college}
                onChange={handleChange}
              />
              {errors.college && <p className="error">{errors.college}</p>}

              <label>Upload Students Excel (optional)</label>
              <input
                type="file"
                name="excelFile"
                accept=".xlsx, .xls"
                onChange={handleExcelUpload}
              />
            </>
          )}

          {/* Purpose */}
          <textarea
            name="purpose"
            placeholder="Purpose of Visit"
            value={form.purpose}
            onChange={handleChange}
          ></textarea>
          {errors.purpose && <p className="error">{errors.purpose}</p>}

          {/* Preferred Location */}
          <select
            name="preferred_location"
            value={form.preferred_location}
            onChange={handleChange}
          >
            <option value="">Select Preferred Location</option>
            <option value="Namakkal">Namakkal</option>
            <option value="Bangalore">Bangalore</option>
          </select>
          {errors.preferred_location && (
            <p className="error">{errors.preferred_location}</p>
          )}

          {/* Industrial Visit → MUI Date Picker */}
          {form.visit_type === "industrial_visit" && (
            <>
              <label>Select Date of Visit</label>

              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Choose Friday or Saturday"
                  value={form.visit_date}
                  minDate={new Date()}
                  shouldDisableDate={(date) => {
                    const day = date.getDay();
                    return !(day === 5 || day === 6);
                  }}
                  onChange={(newValue) => {
                    // Clear any previous date error
                    const newErrors = { ...errors };
                    delete newErrors.visit_date;
                    setErrors(newErrors);

                    // Validate day is Friday (5) or Saturday (6)
                    if (newValue) {
                      const day = newValue.getDay();
                      if (day === 5 || day === 6) {
                        setForm({ ...form, visit_date: newValue });
                      } else {
                        // If invalid day, clear the date and show error
                        setForm({ ...form, visit_date: null });
                        setErrors({ 
                          ...newErrors, 
                          visit_date: "Only Friday or Saturday allowed" 
                        });
                      }
                    } else {
                      setForm({ ...form, visit_date: newValue });
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      inputProps={{
                        ...params.inputProps,
                        readOnly: true, // Prevent manual typing
                      }}
                      sx={{
                        marginTop: "10px",
                        background: "#fff",
                        borderRadius: "8px",
                      }}
                    />
                  )}
                />
              </LocalizationProvider>

              {errors.visit_date && (
                <p className="error">{errors.visit_date}</p>
              )}
            </>
          )}

          {/* Inplant Training → Duration */}
          {form.visit_type === "inplant_training" && (
            <>
              <label>Select Training Duration</label>
              <select
                name="training_duration"
                value={form.training_duration}
                onChange={handleChange}
              >
                <option value="">Select Duration</option>
                <option value="15_days">15 Days</option>
                <option value="30_days">30 Days</option>
              </select>
              {errors.training_duration && (
                <p className="error">{errors.training_duration}</p>
              )}
            </>
          )}

          {/* Submit */}
          <div className="sticky-submit">
            <button type="submit" disabled={submitting}>
              {submitting ? "Submitting..." : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EducationPurpose;